import React from 'react';
import type { CollectionCategory, ItemsPerRow } from '../utils/types';
import { tileGridStyle } from '../utils/gridStyles';
import { safeUrl, openInWorkingTab } from '../utils/navigate';
import { ga4Event } from '../utils/ga4';
import { useFontSizes } from '../contexts/FontSizeContext';

/** Stable gradient palette used behind tiles while images load (or as fallback). */
const GRADIENTS = [
  'from-red-800 to-red-900',
  'from-purple-700 to-purple-800',
  'from-emerald-600 to-emerald-700',
  'from-indigo-600 to-indigo-700',
  'from-rose-400 to-rose-500',
  'from-teal-500 to-teal-600',
  'from-slate-600 to-slate-700',
  'from-sky-400 to-sky-500',
];

interface CollectionTilesProps {
  collections: CollectionCategory[];
  itemsPerRow?: ItemsPerRow;
}

/**
 * Image tiles for curated collections (imported by category-type feeds).
 * Each tile links to its collection page — same navigation rules as
 * product cards (safe http(s) URLs, working-tab behavior).
 */
export const CollectionTiles: React.FC<CollectionTilesProps> = ({ collections, itemsPerRow }) => {
  const { small } = useFontSizes();
  if (collections.length === 0) return null;

  return (
    <div style={tileGridStyle('small', itemsPerRow ?? 4, 12)}>
      {collections.map((col, index) => {
        const href = safeUrl(col.url);
        const gradient = GRADIENTS[index % GRADIENTS.length];
        return (
          <a
            key={col.id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              ga4Event('select_content', { content_type: 'collection', content_id: col.id });
              openInWorkingTab(href, e);
            }}
            title={col.description || col.name}
            className={[
              'group relative flex items-end justify-start overflow-hidden transition-all',
              'h-24 sm:h-28 bg-gradient-to-br', gradient,
              'focus:outline-none focus:ring-2 focus:ring-brand/30 focus:ring-offset-2',
              'animate-fade-in',
            ].join(' ')}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            {col.imageUrl && (
              <img
                src={col.imageUrl}
                alt={col.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            {/* Legibility scrim over the image */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
            <span className={`relative ${small} font-semibold text-white p-2 sm:p-2.5 leading-tight line-clamp-2`}>
              {col.name}
            </span>
          </a>
        );
      })}
    </div>
  );
};
